import { computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';

import { useActions, useState, useGetters } from '~coreModules/core/js/composables/vuexHelpers';

import useAnalyticsPageView from '~coreModules/analytics/js/composables/use-analytics-pageview';
import { getProductModuleNameFromRoute } from '~coreRoutes/product/js/product-utils';
import { getProductBrandName } from '~routes/product/js/product-utils';

import { GLOBAL_EVENT } from '~coreModules/core/js/store';
import { VIEW_PRODUCT } from '~coreModules/core/js/global-event-constants';

export default function useProductAnalytics() {
    const route = useRoute();

    const productModuleName = computed(() => getProductModuleNameFromRoute(route));

    const { trackGlobalEvent } = useActions({ trackGlobalEvent: GLOBAL_EVENT });

    const {
        displayName,
        slug,
        styleNumber,
        numReviews,
        numReviewImages,
        averageRating,
        nuulyClass,
        originalPrice,
        color,
        traceId,
        brand,
    } = useState(productModuleName.value, {
        displayName: state => state.productDetails?.displayName || '',
        slug: state => state.productDetails?.slug || '',
        styleNumber: state => state.productDetails?.styleNumber || '',
        numReviews: state => state.productDetails?.reviewSummary?.reviewCount || 0,
        numReviewImages: state => state.productDetails?.reviewSummary?.imageCount || 0,
        averageRating: state => state.productDetails?.reviewSummary?.overallRatingAverage || 0,
        nuulyClass: state => state.productDetails?.facets?.nuulyClass?.[0] || '',
        originalPrice: state => state.productDetails?.msrpValue || '',
        color: state => state.colorCode,
        traceId: state => state.traceId,
        brand: state => getProductBrandName(state.productDetails?.brandUrls || []),
    });

    const {
        isProductSoldOut,
        selectedChoice,
        productAnalyticsData,
    } = useGetters(productModuleName.value, [
        'isProductSoldOut',
        'selectedChoice',
        'productAnalyticsData',
    ]);

    function getAnalyticsData() {
        return {
            displayName: displayName.value,
            slug: slug.value,
            styleNumber: styleNumber.value,
            numReviews: numReviews.value,
            numReviewImages: numReviewImages.value,
            averageRating: averageRating.value,
            class: nuulyClass.value,
            originalPrice: originalPrice.value,
            color: color.value,
            traceId: traceId.value,
            brand: brand.value,
            isProductSoldOut: isProductSoldOut.value,
            selectedChoice: selectedChoice.value,
            productAnalyticsData: productAnalyticsData.value,
        };
    }

    const { firePageview } = useAnalyticsPageView({ getAnalyticsData });

    watch(() => route.query.view, (newView, oldView) => {
        if (newView !== oldView) {
            firePageview();

            trackGlobalEvent({
                type: VIEW_PRODUCT,
                data: productAnalyticsData.value,
            });
        }
    });

    onMounted(() => {
        trackGlobalEvent({
            type: VIEW_PRODUCT,
            data: productAnalyticsData.value,
        });
    });
}
